import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings2, Edit, Download, History, Receipt, Plus } from 'lucide-react';
import { RateCardSheet } from '@/components/RateCard';
import { useBudgetCalculations } from '@/hooks/useBudgetCalculations';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatPercent, cn } from '@/lib/utils';
import { ColumnConfiguratorDrawer } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { 
  COST_BUDGET_COLUMNS, 
  COST_BUDGET_PRESETS,
  COST_BUDGET_STORAGE_KEY 
} from '@/lib/cost-budget-columns';
import { exportCostBudgetToExcel } from '@/lib/cost-budget-export-utils';
import type { CostCategory } from '@/types/budget.types';

interface CostBudgetTableProps {
  projectId: string;
  categories: CostCategory[];
  totals?: {
    budget: number;
    actual: number;
    committed: number;
    available: number;
    progressPercent: number;
  };
  resourceBudget?: any;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
}

export function CostBudgetTable({ 
  projectId,
  categories = [],
  totals,
  resourceBudget,
  onEdit,
  onExport,
  onHistory 
}: CostBudgetTableProps) {
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);

  const { totalLabourCost } = useBudgetCalculations(
    projectId,
    resourceBudget || null
  );

  // Calculate totals from categories if not provided
  const totalBudget = totals?.budget ?? categories.reduce((sum, cat) => sum + (cat.budget || 0), 0);
  const totalActual = totals?.actual ?? categories.reduce((sum, cat) => sum + (cat.actual || 0), 0);
  const totalCommitted = totals?.committed ?? categories.reduce((sum, cat) => sum + (cat.committed || 0), 0);
  const totalRemaining = totalBudget - totalActual - totalCommitted;
  // Handle division by zero - if budget is 0, show 0% instead of NaN
  const totalPercentUsed = totalBudget > 0 ? ((totalActual + totalCommitted) / totalBudget) * 100 : 0;

  // Helper function to get status badge based on % used
  // Aligned with Resource Budget status thresholds:
  // - On Track: 0-79% used (Green)
  // - At Risk: 80-99% used (Amber/Yellow)
  // - Over Budget: 100%+ used (Red)
  // Special case: If budget is 0 and no actual/committed costs, show "No Budget"
  const getStatusBadge = (percentUsed: number, budget: number, actual: number, committed: number | null) => {
    // If budget is 0 and there are no costs, show "No Budget" (nothing to track)
    if (budget === 0 && actual === 0 && (committed === 0 || committed === null)) {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          No Budget
        </Badge>
      );
    }
    
    // If budget is 0 but there are costs, show "Over Budget"
    if (budget === 0 && (actual > 0 || (committed && committed > 0))) {
      return (
        <Badge variant="destructive" accent="destructive">
          Over Budget
        </Badge>
      );
    }
    
    if (percentUsed < 80) {
      return (
        <Badge variant="success" accent="success">
          On Track
        </Badge>
      );
    } else if (percentUsed < 100) {
      return (
        <Badge variant="warning" accent="warning">
          At Risk
        </Badge>
      );
    } else {
      return (
        <Badge variant="destructive" accent="destructive">
          Over Budget
        </Badge>
      );
    }
  };

  // Helper function to calculate remaining and format with color
  const calculateRemaining = (budget: number, actual: number, committed: number | null) => {
    const remaining = budget - actual - (committed || 0);
    return {
      value: remaining,
      className: remaining >= 0 ? 'text-success-600' : 'text-error-600',
    };
  };

  // Handle export
  const handleExport = async () => {
    if (onExport) {
      onExport();
    } else {
      // Default export behavior
      const exportData = categories.map(cat => {
        const budget = cat.budget || 0;
        const actual = cat.actual || 0;
        const committed = cat.committed || 0;
        // Handle division by zero
        const percentUsed = budget > 0 ? ((actual + committed) / budget) * 100 : 0;
        const remaining = budget - actual - committed;
        let status = 'On Track';
        if (budget === 0 && actual === 0 && committed === 0) {
          status = 'No Budget';
        } else if (budget === 0 || percentUsed >= 100) {
          status = 'Over Budget';
        } else if (percentUsed >= 80) {
          status = 'At Risk';
        }

        return {
          category: cat.name,
          budget,
          actual,
          committed,
          remaining,
          percentUsed,
          status,
        };
      });

      await exportCostBudgetToExcel(exportData);
    }
  };

  // Show empty state if no categories
  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Card className="w-full max-w-md border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center p-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Receipt className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Cost Budget</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Track expenses, commitments, and cost categories by creating a cost budget for this project.
            </p>
            {onEdit && (
              <Button onClick={onEdit}>
                <Plus className="mr-2 h-4 w-4" />
                Create Cost Budget
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Cost Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            <ColumnConfigButton />
          </div>
        </CardHeader>
        <CardContent>
          <CostBudgetTableView 
            categories={categories}
            totalBudget={totalBudget}
            totalActual={totalActual}
            totalCommitted={totalCommitted}
            totalRemaining={totalRemaining}
            totalPercentUsed={totalPercentUsed}
            totalLabourCost={totalLabourCost}
            getStatusBadge={getStatusBadge}
            calculateRemaining={calculateRemaining}
          />
        </CardContent>
      </Card>

      {/* Rate Card Sheet */}
      <RateCardSheet
        open={isRateCardOpen}
        onOpenChange={setIsRateCardOpen}
        projectId={projectId}
      />
    </>
  );
}

// Column Configuration Button Component
function ColumnConfigButton() {
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: COST_BUDGET_STORAGE_KEY,
    defaultColumns: COST_BUDGET_COLUMNS,
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsConfigOpen(true)}
        className="gap-2"
      >
        <Settings2 className="h-4 w-4" />
        Configure Columns
      </Button>
      <ColumnConfiguratorDrawer
        open={isConfigOpen}
        onOpenChange={setIsConfigOpen}
        columns={columns}
        onSave={updateColumns}
        presets={COST_BUDGET_PRESETS}
        defaultColumns={COST_BUDGET_COLUMNS}
      />
    </>
  );
}

// Cost Budget Table View Component
interface CostBudgetTableViewProps {
  categories: CostCategory[];
  totalBudget: number;
  totalActual: number;
  totalCommitted: number;
  totalRemaining: number;
  totalPercentUsed: number;
  totalLabourCost: number;
  getStatusBadge: (percentUsed: number, budget: number, actual: number, committed: number | null) => React.ReactElement;
  calculateRemaining: (budget: number, actual: number, committed: number | null) => { value: number; className: string };
}

function CostBudgetTableView({
  categories,
  totalBudget,
  totalActual,
  totalCommitted,
  totalRemaining,
  totalPercentUsed,
  totalLabourCost,
  getStatusBadge,
  calculateRemaining,
}: CostBudgetTableViewProps) {
  const { columns, isLoading } = useColumnPreferences({
    storageKey: COST_BUDGET_STORAGE_KEY,
    defaultColumns: COST_BUDGET_COLUMNS,
  });

  if (isLoading) {
    return <p className="text-muted-foreground text-center py-8">Loading...</p>;
  }

  // Get visible columns in order
  const visibleColumns = columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);

  // Transform categories into rows with all column data
  const rows = categories.map((category) => {
    const budget = category.budget || 0;
    const actual = category.actual || 0;
    const committed = category.committed || 0;
    // Handle division by zero - if budget is 0, show 0% instead of NaN
    const percentUsed = budget > 0 ? ((actual + committed) / budget) * 100 : 0;
    const remaining = calculateRemaining(budget, actual, committed);

    return {
      id: category.id?.toString() || category.name,
      category: category.name,
      budget: formatCurrency(budget),
      actual: formatCurrency(actual),
      committed: committed !== null ? formatCurrency(committed) : '—',
      remaining: { text: formatCurrency(remaining.value), className: remaining.className },
      'percent-used': formatPercent(percentUsed, 1),
      status: getStatusBadge(percentUsed, budget, actual, committed),
      // Raw values for calculations
      _raw: {
        budget,
        actual,
        committed,
        remaining: remaining.value,
        percentUsed,
      },
    };
  });

  // Helper function to render cell content based on column ID
  const renderCell = (columnId: string, row: typeof rows[0]) => {
    if (columnId === 'status') {
      return row.status;
    }
    
    if (columnId === 'remaining') {
      return (
        <span className={cn('font-medium', row.remaining.className)}>
          {row.remaining.text}
        </span>
      );
    }

    // Add "(Auto)" badge for Labour category
    if (columnId === 'category' && row.category === 'Labour' && totalLabourCost > 0) {
      return (
        <div className="flex items-center gap-2">
          <span>{row.category}</span>
          <Badge variant="secondary" className="text-xs">Auto</Badge>
        </div>
      );
    }
    
    return row[columnId as keyof typeof row] as string;
  };

  // Helper function to render total cell content based on column ID
  const renderTotalCell = (columnId: string) => {
    switch (columnId) {
      case 'category':
        return 'Total';
      case 'budget':
        return formatCurrency(totalBudget);
      case 'actual':
        return formatCurrency(totalActual);
      case 'committed':
        return formatCurrency(totalCommitted);
      case 'remaining':
        return (
          <span className={cn('font-bold', totalRemaining >= 0 ? 'text-success-600' : 'text-error-600')}>
            {formatCurrency(totalRemaining)}
          </span>
        );
      case 'percent-used':
        return formatPercent(totalPercentUsed, 1);
      case 'status':
        return getStatusBadge(totalPercentUsed, totalBudget, totalActual, totalCommitted);
      default:
        return '';
    }
  };

  // Helper function to get alignment class
  const getAlignmentClass = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (columnId === 'status') return 'text-right';
    return column?.align === 'right' ? 'text-right' : '';
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {visibleColumns.map((column) => (
              <TableHead 
                key={column.id} 
                className={`font-bold ${getAlignmentClass(column.id)}`}
              >
                {column.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {visibleColumns.map((column) => (
                <TableCell 
                  key={column.id} 
                  className={`${column.id === 'category' ? 'font-medium' : ''} ${getAlignmentClass(column.id)} ${column.id !== 'category' && column.id !== 'status' && column.id !== 'remaining' ? 'tabular-nums' : ''}`}
                >
                  {renderCell(column.id, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <TableRow className="font-bold bg-muted/50 border-t-2">
            {visibleColumns.map((column) => (
              <TableCell 
                key={column.id} 
                className={`${getAlignmentClass(column.id)} ${column.id !== 'category' && column.id !== 'status' && column.id !== 'remaining' ? 'tabular-nums' : ''}`}
              >
                {renderTotalCell(column.id)}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
